import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articleList } from "@/mocks/articles";

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setGuideOpen(false);
  }, [location.pathname]);

  const navTextClass = isHome && !scrolled
    ? "text-background-50"
    : "text-foreground-950";

  const navBorderClass = scrolled
    ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
    : "bg-transparent";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBorderClass}`}
      >
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <span className={`text-lg md:text-xl font-heading font-bold tracking-tight leading-tight transition-colors duration-300 ${navTextClass}`}>
                {t("nav.brand")}
              </span>
              <span className={`hidden sm:inline text-xs font-body font-medium tracking-[0.2em] uppercase transition-colors duration-300 ${isHome && !scrolled ? "text-background-100/70" : "text-foreground-600"}`}>
                {t("nav.brand_sub")}
              </span>
            </button>

            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={() => navigate("/")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${isHome && !scrolled ? "text-background-100/80 hover:text-background-50 hover:bg-background-50/10" : "text-foreground-600 hover:text-foreground-950 hover:bg-background-100"}`}
              >
                {t("nav.home")}
              </button>

              <div className="relative" ref={guideRef}>
                <button
                  onClick={() => setGuideOpen(!guideOpen)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${isHome && !scrolled ? "text-background-100/80 hover:text-background-50 hover:bg-background-50/10" : "text-foreground-600 hover:text-foreground-950 hover:bg-background-100"}`}
                >
                  <span>{t("nav.shopping_guide")}</span>
                  <i
                    className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideOpen ? "rotate-180" : ""}`}
                  ></i>
                </button>

                {guideOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden py-2 shadow-lg">
                    <p className="px-4 py-1.5 text-xs text-foreground-400 tracking-wider uppercase font-medium">
                      {t("nav.guide_heading")}
                    </p>
                    {articleList.map((a) => (
                      <button
                        key={a.slug}
                        onClick={() => { navigate(`/article/${a.slug}`); setGuideOpen(false); }}
                        className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {a.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-5">
            <div className="relative hidden md:block" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
                  isHome && !scrolled
                    ? "bg-background-50/15 border-background-50/25 text-background-50 hover:bg-background-50/25"
                    : "bg-background-100 border-background-200/70 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <span>{t("nav.dropdown_placeholder")}</span>
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden max-h-80 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="hidden md:flex items-center gap-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
                  isHome && !scrolled
                    ? "bg-background-50 text-foreground-950 hover:bg-background-100"
                    : "bg-foreground-950 text-background-50 hover:bg-foreground-800"
                }`}
              >
                {t("nav.btn_oopbuy")}
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
                  isHome && !scrolled
                    ? "border border-background-50/60 text-background-50 hover:bg-background-50/10"
                    : "border border-foreground-300 text-foreground-800 hover:bg-foreground-50"
                }`}
              >
                {t("nav.btn_buy")}
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(true)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                isHome && !scrolled
                  ? "text-background-50 hover:bg-background-50/10"
                  : "text-foreground-950 hover:bg-background-100"
              }`}
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div className="absolute top-0 right-0 w-80 max-w-[85vw] h-full bg-background-50 flex flex-col overflow-y-auto">
            <div className="flex items-center justify-between px-4 h-16 border-b border-background-200/60">
              <span className="text-lg font-heading font-bold text-foreground-950">
                {t("nav.brand")}
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer text-foreground-700 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 px-4 py-4 space-y-4">
              <button
                onClick={() => { navigate("/"); setMobileOpen(false); }}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-home-4-line text-lg"></i>
                <span>{t("nav.home")}</span>
              </button>

              <div>
                <p className="px-3 py-1 text-xs font-semibold text-foreground-400 tracking-wider uppercase">
                  {t("nav.shopping_guide")}
                </p>
                <div className="space-y-0.5 mt-1">
                  {articleList.map((a) => (
                    <button
                      key={a.slug}
                      onClick={() => { navigate(`/article/${a.slug}`); setMobileOpen(false); }}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
                    >
                      {a.title}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-background-200/60 pt-4">
                <p className="px-3 py-1 text-xs font-semibold text-foreground-400 tracking-wider uppercase">
                  {t("nav.dropdown_placeholder")}
                </p>
                <div className="space-y-0.5 mt-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-background-200/60 p-4 space-y-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-4 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                {t("nav.btn_oopbuy")}
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-4 py-3 rounded-full border border-foreground-200 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {t("nav.btn_buy")}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}